import { Box, CardBody, Tag } from '@chakra-ui/react';
import type { SortingState } from '@tanstack/react-table';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import { dimensionSourceToFilterId } from '../../../utils/dimension-source-serialization';
import { isScreenerV2Page } from '../../../utils/routing-helpers';
import type { MultiMetricProviderResult } from '../../DataProvider/MultiMetricProvider';
import { ScreenerTableMultiMetricProvider } from '../../DataProvider/ScreenerTableMultiMetricProvider';
import { SnowflakeDataProvider } from '../../DataProvider/SnowflakeDataProvider';
import { TableMultiMetricProvider } from '../../DataProvider/TableMultiMetricProvider';
import { DataType } from '../../DataProvider/types';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import {
  type TableRow,
  useTableSelection,
} from '../../TableSelectionProvider/TableSelectionProvider';
import { TrialUnblurProvider } from '../../TrialUnblurProvider/TrialUnblurProvider';
import { ChartFallback } from '../ChartFallback';
import {
  ChartHeader,
  ChartHeaderTitle,
  ChartHeaderTitleInfoWrapper,
  ChartTitleInfo,
} from '../ChartHeader';
import { useChart } from '../ChartProvider';
import { ChartCard, type ChartProps } from '../ChartRoot/Chart';
import { ChartActionMenuWrapper } from '../chart-menu/ChartActionMenuWrapper';
import { processMultiMetricData } from '../utils/process-multi-metric-data';
import { convertTableData } from './convert-table-data';
import { ReportTableCard } from './ReportTableCard';
import { ScreenerTableChartContent } from './ScreenerTableChartContent';
import { SelectAllCheckbox } from './SelectAllCheckbox';
import { Table } from './Table';
import styles from './TableChart.module.css';
import { TableChartExportMeta } from './TableChartExportMeta';
import { TableControlsProvider } from './TableControlsContext';
import { TalentDiscoveryCardView } from './TalentDiscoveryCardView';
import { getPrimaryColumnHeader } from './utils/table-helpers';
import { ViewModeToggle } from './ViewModeToggle';

type TableChartProps = Pick<
  ChartProps,
  'item' | 'title' | 'onClick' | 'primaryFilter'
> & {
  data?: MultiMetricProviderResult;
  snowflakeRows?: TableRow[];
  onSelectRow?: (data: object) => void;
};

const getPrimaryDimension = (
  revelioPageId: RevelioPageId | undefined
): PrimaryFilterId | null => {
  if (revelioPageId === RevelioPageId.COMPANY_SCREENER_V2) {
    return FilterId.COMPANY;
  }
  if (revelioPageId === RevelioPageId.GEOGRAPHY_SCREENER_V2) {
    return FilterId.GEOGRAPHY;
  }
  if (revelioPageId === RevelioPageId.ROLE_SCREENER_V2) {
    return FilterId.ROLE;
  }
  return null;
};

export const TableChart = (props: TableChartProps) => {
  const { revelioPageId } = useTabContentConfig();

  const isScreenerPage = isScreenerV2Page(revelioPageId);
  const screenerPrimaryDimension = isScreenerPage
    ? getPrimaryDimension(revelioPageId)
    : null;

  if (isScreenerPage) {
    return (
      <TableControlsProvider>
        <ScreenerTableMultiMetricProvider
          primaryDimension={screenerPrimaryDimension ?? undefined}
        >
          {({ data, status }) => (
            <ChartFallback status={status} onClick={props.onClick}>
              <ScreenerTableChartContent {...props} data={data} />
            </ChartFallback>
          )}
        </ScreenerTableMultiMetricProvider>
      </TableControlsProvider>
    );
  }

  const dataSource = props.item?.plot?.dataSource;
  const snowflakeConfig = props.item?.plot?.snowflakeConfig;

  if (dataSource === 'snowflake' && snowflakeConfig) {
    return (
      <TableControlsProvider>
        <SnowflakeDataProvider config={snowflakeConfig} chartType="table">
          {({ data, status }) => {
            const rows: TableRow[] =
              data?.type === DataType.TABLE_ROWS
                ? (data.data as TableRow[])
                : [];

            return (
              <ChartFallback
                status={status}
                onClick={props.onClick ?? undefined}
              >
                <TableChartContent {...props} snowflakeRows={rows} />
              </ChartFallback>
            );
          }}
        </SnowflakeDataProvider>
      </TableControlsProvider>
    );
  }

  return (
    <TableControlsProvider>
      <TableMultiMetricProvider
        primaryDimension={props.primaryFilter?.id}
        secondaryDimension={props.item?.plot?.secondaryDimension}
      >
        {({ data, status }) => (
          <>
            {/* `table` routes through TableChart (not DataWrappedChart), so
                the global-assistant chart-preview overlay needs the table's
                load status reported here for the overlay to clear. */}
            <GlobalAiChartPreviewDataReporter
              itemId={props.item.id}
              branch="multi"
              status={status}
              error={null}
              data={data}
              chartType="table"
              metric={props.item?.plot?.defaultMetricOptions?.[0]}
            />
            <ChartFallback status={status} onClick={props.onClick}>
              <TableDataWithParameterAggregation {...props} data={data} />
            </ChartFallback>
          </>
        )}
      </TableMultiMetricProvider>
    </TableControlsProvider>
  );
};

const TableDataWithParameterAggregation = (props: TableChartProps) => {
  const { data: rawData } = props;
  const { item } = useChart();
  const { config: pageConfig } = usePageConfig();
  const { activeFilterMap } = useFilterContext();

  const data = useMemo(() => {
    const byMetric = rawData?.byMetric;
    if (!byMetric || Object.keys(byMetric).length === 0) {
      return rawData;
    }
    const reportParameters =
      pageConfig?.tabs?.[0]?.tabContent?.reportParameters ?? [];
    const plotParameters = item?.plot?.plotParameters ?? [];
    const aggregatedByMetric = processMultiMetricData({
      byMetric,
      plotParameters,
      reportParameters,
      activeFilterMap,
    });
    return { byMetric: aggregatedByMetric };
  }, [rawData, item?.plot?.plotParameters, pageConfig?.tabs, activeFilterMap]);

  return <TableChartContent {...props} data={data} />;
};

const TableChartContent = ({
  item,
  title,
  data,
  snowflakeRows,
  primaryFilter,
  onClick,
  onSelectRow,
}: TableChartProps) => {
  const { variant } = useChart();
  const isReport = variant === 'report';
  const { revelioPageId } = useTabContentConfig();
  const isTalentDiscoveryPage =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY;
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [sortedTableData, setSortedTableData] = useState<TableRow[]>([]);
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const { setSelectedRows, selectedRows } = useTableSelection();

  const { tableData, columnMetaMap } = useMemo(() => {
    if (snowflakeRows !== undefined) {
      return { tableData: snowflakeRows, columnMetaMap: undefined };
    }

    const effectiveScreenerDimension =
      item?.plot?.dimensionSource && item.plot.dimensionSource !== 'primary'
        ? dimensionSourceToFilterId(item.plot.dimensionSource)
        : undefined;

    const effectivePrimaryDimension =
      effectiveScreenerDimension ?? primaryFilter?.id;

    const result = convertTableData({
      data: data?.byMetric,
      secondaryDimension: item?.plot?.secondaryDimension,
      primaryDimension: effectivePrimaryDimension,
    });

    if (Array.isArray(result)) {
      return { tableData: result, columnMetaMap: undefined };
    }

    const rows = (() => {
      if (!effectiveScreenerDimension) return result.rows;

      const columnName = getPrimaryColumnHeader(effectivePrimaryDimension);
      if (columnName === 'Primary') return result.rows;

      return result.rows.map((row) => {
        if (!('Primary' in row)) return row;
        const { Primary, ...rest } = row;
        return { [columnName]: Primary, ...rest };
      });
    })();

    return { tableData: rows, columnMetaMap: result.columnMetaMap };
  }, [
    snowflakeRows,
    data?.byMetric,
    item?.plot?.secondaryDimension,
    item?.plot?.dimensionSource,
    primaryFilter?.id,
  ]);

  const handleViewModeChange = useCallback((mode: 'list' | 'card') => {
    setViewMode(mode);
  }, []);

  const hasActiveSorting = sortingState.length > 0;

  useEffect(() => {
    if (viewMode !== 'card' || hasActiveSorting) {
      return;
    }
    setSortedTableData(tableData);
  }, [hasActiveSorting, tableData, viewMode]);

  const exportData = useMemo(
    () =>
      tableData.length > 0
        ? { type: DataType.TABLE_ROWS, data: tableData }
        : undefined,
    [tableData]
  );

  const handleSortedDataChange = useCallback((rows: TableRow[]) => {
    setSortedTableData(rows);
  }, []);

  const cardData = hasActiveSorting ? sortedTableData : tableData;

  const numOfRecords = tableData.length;

  const showNumOfRecords = numOfRecords > 0;

  const tagLabel =
    numOfRecords >= 5000
      ? `More than ${numOfRecords.toLocaleString()}`
      : numOfRecords.toLocaleString();

  const onRowSelectionChange = useCallback(
    (rows: TableRow[]) => {
      setSelectedRows(rows);
    },
    [setSelectedRows]
  );

  const showZebraStripe = item?.plot?.showZebraStripe;
  const enableRowSelection = item?.plot?.enableRowSelection !== false;

  const tableContent =
    viewMode === 'card' ? (
      <Box
        h="100%"
        overflow="hidden"
        className={classNames(styles.cardViewContainer)}
      >
        <TalentDiscoveryCardView
          data={cardData}
          enableRowSelection={enableRowSelection}
          onRowSelectionChange={onRowSelectionChange}
          selectedRows={selectedRows}
          showZebraStripe={showZebraStripe}
          popoutChartType={
            item?.plot?.tableRowPopout?.chartType ?? 'full-profile-info'
          }
        />
      </Box>
    ) : (
      <Table
        data={tableData}
        columnMetaMap={columnMetaMap}
        onSelectRow={onSelectRow}
        enableRowSelection={enableRowSelection}
        onSortedDataChange={
          isTalentDiscoveryPage ? handleSortedDataChange : undefined
        }
        onSortingStateNotify={
          isTalentDiscoveryPage ? setSortingState : undefined
        }
      />
    );

  const wrappedTableContent = isTalentDiscoveryPage ? (
    <TrialUnblurProvider>{tableContent}</TrialUnblurProvider>
  ) : (
    tableContent
  );

  const tableExportEnabled = Boolean(exportData);

  const exportRegistration = item?.id ? (
    <>
      <ChartActionMenuWrapper
        chartId={item.id}
        title={title || 'Table'}
        primaryFilterId={primaryFilter?.id}
        plotChartType="table"
        data={exportData}
        dataType={exportData ? DataType.TABLE_ROWS : undefined}
      />
      <TableChartExportMeta
        chartId={item.id}
        enabled={tableExportEnabled}
        columnMetaMap={columnMetaMap}
        plotFormat={item?.plot?.format}
      />
    </>
  ) : null;

  if (isReport) {
    return (
      <>
        {exportRegistration}
        <ReportTableCard title={title} onClick={onClick}>
          {wrappedTableContent}
        </ReportTableCard>
      </>
    );
  }

  return (
    <>
      {exportRegistration}
      <ChartCard onClick={onClick} position="relative">
        <ChartHeader
          paddingLeft="0.875rem"
          paddingRight="0.875rem"
          gap="0"
          fontSize="1rem"
          lineHeight="44px"
        >
          <ChartHeaderTitleInfoWrapper
            {...(item.info && {
              chartTitleInfo: <ChartTitleInfo info={item.info} />,
            })}
            width="100%"
            border="1px solid #E2E8F0"
            borderBottom="none"
            borderTopRadius="6px"
            alignItems="center"
            justifyContent="space-between"
            px="0.5rem"
          >
            <ChartHeaderTitle>
              {enableRowSelection && tableData.length > 0 && (
                <SelectAllCheckbox
                  tableData={tableData}
                  selectedRows={selectedRows}
                  onRowSelectionChange={onRowSelectionChange}
                />
              )}
              {title}
              {showNumOfRecords && (
                <Tag
                  size="sm"
                  backgroundColor="transparent"
                  border="1px solid #E2E8F0"
                  borderRadius="full"
                  marginRight="0"
                  padding="0 6px"
                  height="16px"
                  minHeight="16px"
                >
                  {tagLabel}
                </Tag>
              )}
            </ChartHeaderTitle>
            {isTalentDiscoveryPage && tableData.length > 0 && (
              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
              />
            )}
          </ChartHeaderTitleInfoWrapper>
        </ChartHeader>
        <CardBody
          data-testid="card-body"
          padding="0 14px 0.5rem"
          height="calc(100% - 57px)"
        >
          {wrappedTableContent}
        </CardBody>
      </ChartCard>
    </>
  );
};
